import { Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core'
import { VideoPlayerComponent } from '../video-player/video-player.component'
import { AudioPlayerComponent } from '../audio-player/audio-player.component'
import { Call, CallsService, TranscriptSpeakerSlot, MetricType } from '../calls.service'
import { distinctUntilChanged, filter, interval, map, Subscription, tap } from 'rxjs'
import { ActivatedRoute, Router } from '@angular/router'
import { LoggerService } from '../logger.service'
import { useMediaTimePrecision } from '../media-player-controller/media-player-controller.component'
import { MetricsService } from '../metrics.service'

@Component({
  selector: 'app-media-player',
  standalone: true,
  imports: [VideoPlayerComponent, AudioPlayerComponent],
  templateUrl: './media-player.component.html',
  styleUrl: './media-player.component.scss',
})
export class MediaPlayerComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  call!: Call

  @Input({ required: true })
  shouldNavigateTranscriptBasedOnMediaTime!: boolean

  @Input({ required: true })
  labelingModeEnabled!: boolean

  @ViewChild(VideoPlayerComponent)
  videoPlayer: VideoPlayerComponent | undefined = undefined

  @ViewChild(AudioPlayerComponent)
  audioPlayer: AudioPlayerComponent | undefined = undefined

  router = inject(Router)
  route = inject(ActivatedRoute)
  loggerService = inject(LoggerService)
  metricsService = inject(MetricsService)
  callsService = inject(CallsService)
  private trackMediaTimestampWhenPlayingSubscription: Subscription | null = null

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['labelingModeEnabled'] && this.labelingModeEnabled) {
      this.play()
    }
  }

  ngOnInit(): void {
    this.trackMediaTimestampWhenPlaying()
    this.trackMediaTimeQueryParam()
  }

  play() {
    this.videoPlayer?.play()
    this.audioPlayer?.play()
  }

  private trackMediaTimeQueryParam() {
    this.route.queryParams
      .pipe(
        map((params) => params['mediaTime'] ?? null),
        filter((mediaTime) => mediaTime !== null),
        filter((mediaTimeParam) => {
          const mediaTime = parseFloat(mediaTimeParam)
          return mediaTime - 1 < this.getTime() && this.getTime() < mediaTime + 1
        }),
        distinctUntilChanged()
      )
      .subscribe(() => {
        if (!this.getIsPlaying() || !this.shouldNavigateTranscriptBasedOnMediaTime) {
          const reason = !this.getIsPlaying() ? 'the media is not playing' : 'the transcript tracking being disabled'
          this.loggerService.debug(`Not updating the transcript focus placement based on the media time due to ${reason}.`)
          return
        }
        this.updateTranscriptToMatchMediaTime()
      })
  }

  updateTranscriptToMatchMediaTime() {
    const playingSentence =
      this.transcriptSpeakerSlots.flatMap((slot) => slot.sentences).findLast((sentence) => sentence.startTimeSeconds <= this.getTime()) ?? null
    const currentlyActiveSentenceId = this.route.snapshot.queryParamMap.get('sentenceId') ?? null
    if (playingSentence !== null && playingSentence?.sentenceId !== currentlyActiveSentenceId) {
      this.loggerService.info(`Updating the transcript focus placement to the sentence ${playingSentence.sentenceId}.`)
      this.loggerService.sensitive(playingSentence.content)
      setTimeout(() => {
        this.router.navigate([], {
          queryParams: { sentenceId: playingSentence.sentenceId, endSentenceId: null, highlight: true },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        })
      })
    }
  }

  get transcriptSpeakerSlots() {
    return this.call.transcript.filter((slot) => slot.type === 'speaker') as TranscriptSpeakerSlot[]
  }

  private get player() {
    return this.audioPlayer ?? this.videoPlayer ?? null
  }

  getTime(): number {
    return this.player?.getTime() ?? 0
  }

  getDuration() {
    return this.player?.getDuration() ?? 0
  }

  getIsPlaying() {
    return this.player?.isPlaying ?? false
  }

  updateMediaTimeQueryParameter() {
    const currentTime = this.getTime()
    if (currentTime === 0) {
      return
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { mediaTime: useMediaTimePrecision(currentTime) },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
  }

  jumpToNextSpeaker(currentTime: number) {
    const deltaForPrecision = 0.01
    const nextSpeaker = this.call.speakers.find((speaker) => speaker.startTimeSeconds > currentTime + deltaForPrecision) ?? null

    if (nextSpeaker === null) {
      return
    }

    const nextSentence =
      this.transcriptSpeakerSlots.flatMap((slot) => slot.sentences).find((sentence) => sentence.startTimeSeconds >= nextSpeaker.startTimeSeconds) ?? null

    if (nextSentence === null) {
      return
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        mediaTime: useMediaTimePrecision(nextSpeaker.startTimeSeconds),
        sentenceId: nextSentence.sentenceId,
        forceMediaTimeUpdate: true,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
    this.metricsService.sendMetric(this.call.id, MetricType.CLICK_SKIP_TO_NEXT_SPEAKER_BUTTON)
  }

  ngOnDestroy() {
    this.trackMediaTimestampWhenPlayingSubscription?.unsubscribe()
  }

  private trackMediaTimestampWhenPlaying() {
    this.trackMediaTimestampWhenPlayingSubscription = interval(500).subscribe(() => {
      if (!this.getIsPlaying()) {
        return
      }
      this.updateMediaTimeQueryParameter()
    })
  }
}
